import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { TEAM_AVATARS } from '../../components/teamAvatars';
import { getRef } from '../../lib/referral';

/* Cinematic director for Presentation Mode:
   1) CinematicIntro — Hollywood opening: epic score + Rachel narration + staggered serif lines.
   2) PresentDirector — a ghost cursor that scrolls, moves, clicks and explains
      each view like a human giving a live demo to a restaurant owner (with voice). */

const API = process.env.REACT_APP_BACKEND_URL;
const voiceCache = new Map();

async function fetchVoiceUrl(text, lang, voice = 'shimmer') {
  const key = `${voice}|${lang}|${text}`;
  if (voiceCache.has(key)) return voiceCache.get(key);
  const r = await fetch(`${API}/api/rest/present/voice`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, lang, voice }),
  });
  if (!r.ok) throw new Error('tts_failed');
  const url = URL.createObjectURL(await r.blob());
  voiceCache.set(key, url);
  return url;
}

/* Shared cinematic score — the surprise starts the moment they connect (voice picker),
   flows under the intro, ducks when Rachel speaks, and slips away discreetly. */
const theme = { audio: null, timer: null, base: 0.18, target: 0.18, killed: false };
function rampTo(effTarget, step = 0.012, ms = 130, thenStop = false) {
  if (!theme.audio) return;
  if (theme.timer) clearInterval(theme.timer);
  theme.timer = setInterval(() => {
    const a = theme.audio;
    if (!a) { clearInterval(theme.timer); return; }
    const diff = effTarget - a.volume;
    if (Math.abs(diff) <= step) {
      a.volume = Math.max(0, Math.min(1, effTarget));
      clearInterval(theme.timer);
      if (thenStop && effTarget === 0) { a.pause(); theme.audio = null; }
      return;
    }
    a.volume = Math.max(0, Math.min(1, a.volume + Math.sign(diff) * step));
  }, ms);
}
function applyTheme(step = 0.012, ms = 130) { rampTo(theme.target * master.v, step, ms); }
export function startTheme(base = theme.base) {
  theme.killed = false; // a new show brings the score back
  theme.base = base; theme.target = base;
  if (theme.audio) { applyTheme(); return; }
  const a = new Audio('/audio/intro-theme.mp3');
  a.loop = true;
  a.volume = 0;
  theme.audio = a;
  a.play().then(() => applyTheme(0.008, 150)).catch(() => { theme.audio = null; });
}
export function setThemeBase(base) {
  if (theme.killed) return;
  theme.base = base;
  if (liveVoices.size === 0) { theme.target = base; applyTheme(0.004, 140); }
}
export function duckTheme(v = 0.055) { if (theme.killed) return; theme.target = v; applyTheme(0.012, 50); } // quick, musical dip when a voice enters
// Sidechain feel: the score dips quickly when someone speaks, then swells back slowly — natural, respectful.
export function restoreTheme() { if (theme.killed) return; theme.target = theme.base; applyTheme(0.0022, 120); }
export function pauseThemeSoft() {
  if (theme.audio && !theme.audio.paused) { try { theme.audio.pause(); } catch { /* noop */ } }
}
export function resumeThemeSoft() {
  if (theme.killed || !theme.audio) return;
  if (theme.audio.paused) { theme.audio.play().catch(() => {}); applyTheme(0.006, 120); }
}
// The score must NEVER vanish mid-show: revives a stalled/paused theme while the demo runs.
export function ensureTheme() {
  if (theme.killed || document.hidden) return;
  if (!theme.audio) { startTheme(theme.base); return; }
  if (theme.audio.paused) { theme.audio.play().catch(() => {}); applyTheme(0.006, 120); }
}
export function stopTheme(slow = true) {
  theme.killed = true; // once you leave, the music is GONE — nothing revives it
  if (!theme.audio) return;
  theme.target = 0;
  rampTo(0, slow ? 0.012 : 0.05, slow ? 100 : 60, true); // graceful but brief — exit means silence within ~1.5s
}

const master = { v: (() => { try { const s = parseFloat(localStorage.getItem('kx_present_volume')); return Number.isFinite(s) ? Math.min(1, Math.max(0, s)) : 1; } catch { return 1; } })() };
export function getMasterVolume() { return master.v; }
export function setMasterVolume(v) {
  master.v = Math.min(1, Math.max(0, v));
  try { localStorage.setItem('kx_present_volume', String(master.v)); } catch { /* noop */ }
  liveVoices.forEach((a) => { try { a.volume = master.v; } catch { /* noop */ } });
  if (theme.audio) {
    if (theme.timer) clearInterval(theme.timer);
    theme.audio.volume = Math.max(0, Math.min(1, theme.target * master.v));
  }
}

const liveVoices = new Set();
let voiceEpoch = 0; // bumped on every stop — audio fetched before a stop must NEVER play after it
export function getVoiceEpoch() { return voiceEpoch; }
export async function speakLine(text, lang, voice = 'shimmer') {
  const ep = voiceEpoch;
  const url = await fetchVoiceUrl(text, lang, voice);
  if (ep !== voiceEpoch) return { audio: null, done: Promise.resolve() }; // world moved on while fetching
  return playUrl(url, 1);
}
export function stopAllVoices() {
  voiceEpoch += 1;
  liveVoices.forEach((a) => { try { a.pause(); } catch { /* noop */ } });
  liveVoices.clear();
}

function playUrl(url, volume = 1) {
  stopAllVoices(); // only ONE Rachel speaks at a time
  duckTheme(0.055); // the score bows to her voice — present underneath, never competing
  const audio = new Audio(url);
  audio.volume = 0;
  liveVoices.add(audio);
  const done = new Promise((resolve) => {
    const fin = () => { liveVoices.delete(audio); if (liveVoices.size === 0) restoreTheme(); resolve(); };
    audio.addEventListener('ended', fin, { once: true });
    audio.addEventListener('error', fin, { once: true });
    audio.addEventListener('pause', fin, { once: true });
  });
  // Gentle fade-in and fade-out — starts soft, ends soft, never abrupt.
  const fadeIn = setInterval(() => {
    const t = Math.min(1, volume * master.v);
    audio.volume = Math.min(t, audio.volume + Math.max(0.02, t / 14));
    if (audio.volume >= t || audio.paused) clearInterval(fadeIn);
  }, 70);
  const watchOut = setInterval(() => {
    const t = Math.min(1, volume * master.v);
    if (audio.duration && audio.currentTime > audio.duration - 1.3) {
      audio.volume = Math.max(0, audio.volume - Math.max(0.02, t / 14));
    }
    if (audio.ended || audio.paused) clearInterval(watchOut);
  }, 90);
  // Autoplay blocked (no gesture yet)? Resolve immediately so the show never stalls.
  audio.play().catch(() => { audio.dispatchEvent(new Event('error')); });
  return { audio, done };
}

const INTRO_LINES = {
  es: ['No nos conocimos por casualidad.', 'Cada restaurante tiene un momento en el que todo cambia.', 'Este es el tuyo.', 'Acompáñame — esto es lo que vamos a hacer por tu restaurante.'],
  en: ["We didn't meet by accident.", 'Every restaurant has a moment when everything changes.', 'This is yours.', "Sit back and enjoy — this is what we're going to do for your restaurant."],
};
const LINE_MS = [3000, 3400, 2500, 3900];
const INTRO_NARRATION = {
  es: 'Hola. No nos conocimos por casualidad. Cada restaurante tiene un momento en el que todo cambia. Este... es el tuyo. Acompáñame. Esto es lo que vamos a hacer por tu restaurante.',
  en: "Hi. We didn't meet by accident. Every restaurant has a moment when everything changes. This... is yours. Sit back and enjoy. This is what we're going to do for your restaurant.",
};

// The prospect's name — from a dedication or the access form. Rachel uses it all show long.
export function getGuestName() {
  try {
    const raw = sessionStorage.getItem('kx_dedication_to') || localStorage.getItem('kx_welcome_name') || '';
    const n = (raw.trim().split(/\s+/)[0] || '').replace(/[^\p{L}\p{M}'’-]/gu, '').slice(0, 16);
    return n ? n.charAt(0).toUpperCase() + n.slice(1) : '';
  } catch { return ''; }
}
// Exact anchor points where a name lands naturally — never sprayed on every line.
const NAME_HOOKS = [
  ['Hola. No nos conocimos', (n) => `Hola, ${n}. No nos conocimos`],
  ["Hi. We didn't meet", (n) => `Hi, ${n}. We didn't meet`],
  ['Bienvenido.', (n) => `Bienvenido, ${n}.`], ['Welcome.', (n) => `Welcome, ${n}.`],
  ['Tómate tu tiempo con esta.', (n) => `Tómate tu tiempo con esta, ${n}.`],
  ['Take your time with this one.', (n) => `Take your time with this one, ${n}.`],
  ['Deja que hable por sí sola.', (n) => `Deja que hable por sí sola, ${n}.`],
  ['Let it speak for itself.', (n) => `Let it speak for itself, ${n}.`],
];
function personalize(text, name) {
  if (!name) return text;
  for (const [k, fn] of NAME_HOOKS) { if (text.includes(k)) return text.replace(k, fn(name)); }
  return text;
}
function introText(lang) { return personalize(INTRO_NARRATION[lang], getGuestName()); }

// Every cuisine gets its own voice — Rachel names THEIR dishes, never another kitchen's menu.
const PROFILE_DISHES = {
  'casa-mia': { rest: 'Casa Mia', dish: 'Carbonara', dish2: 'Lasagna della Casa' },
  'el-comal': { rest: 'El Comal', dish: 'Tacos al Pastor', dish2: 'Quesadilla Oaxaca' },
  'cafe-luna': { rest: 'Café Luna', dish: 'Latte', dish2: 'Avocado Toast' },
  'arepas': { rest: 'Arepas Latin Cuisine', dish: 'Arepa Reina Pepiada', dish2: 'Tequeños' },
  'hincha': { rest: 'Hincha', dish: 'Bife de Chorizo', dish2: 'Provoleta a la Brasa' },
  'chalito': { rest: 'Chalito', dish: 'Milanesa Napolitana', dish2: 'Chivito Uruguayo' },
  'mila-and-go': { rest: 'Mila & Go', dish: 'Mila Box Clásica', dish2: 'Mila Baguette' },
  'kokoro-sushi': { rest: 'Kokoro Sushi', dish: 'omakase', dish2: 'Nigiri de Salmón' },
  'the-rally': { rest: 'The Rally', dish: 'Buffalo Wings', dish2: 'BBQ Smokehouse Burger',
    vibe: { es: ' y en noches de partido, con el bar lleno, esas alitas vuelan...', en: ' and on game nights, with a packed bar, those wings fly...' } },
};
const TOKEN_FALLBACK = {
  rest: { es: 'tu Restaurant OS', en: 'your Restaurant OS' },
  dish: { es: 'plato estrella', en: 'star dish' },
  dish2: { es: 'plato insignia', en: 'signature dish' },
  vibe: { es: '', en: '' },
};
function contextualize(text, slug, lang) {
  const p = PROFILE_DISHES[slug] || {};
  return text.replace(/\{(rest|dish2|dish|vibe)\}/g, (_, k) => {
    const v = p[k];
    if (v && typeof v === 'object') return v[lang] || v.es;
    return v || (TOKEN_FALLBACK[k] ? TOKEN_FALLBACK[k][lang] : '');
  });
}

// Warm the pipeline while the guest reads the gate — the voice enters on time, never late.
export function prewarmPresentation(lang, voice = 'shimmer', slug = '') {
  const n = getGuestName();
  fetchVoiceUrl(introText(lang), lang, voice).catch(() => {});
  (SCRIPTS[''] || []).slice(0, 2).forEach((s) => fetchVoiceUrl(personalize(contextualize(lang === 'es' ? s.es : s.en, slug, lang), n), lang, voice).catch(() => {}));
}
export function prewarmLine(text, lang, voice) { fetchVoiceUrl(text, lang, voice).catch(() => {}); }

export const VOICES = [
  { id: 'shimmer', label: 'Anne H.', es: 'Cercana · Enérgica · Luminosa', en: 'Friendly · Energetic · Radiant', img: '/avatars/wiki-anne.jpg',
    wiki: 'https://en.wikipedia.org/wiki/Anne_Hathaway',
    bio: { es: 'Actriz estadounidense, Óscar por Les Misérables. El diablo viste de Prada, Interstellar, The Intern.', en: 'American actress, Oscar for Les Misérables. The Devil Wears Prada, Interstellar, The Intern.' },
    sample: { es: '¡Hola! Soy Rachel. Estoy deseando mostrarte todo esto... te va a encantar.', en: "Hi! I'm Rachel. I can't wait to show you all of this... you're going to love it!" } },
  { id: 'coral', label: 'Scarlett J.', es: 'Cálida · Elegante · Envolvente', en: 'Warm · Elegant · Enveloping', img: '/avatars/wiki-scarlett.jpg',
    wiki: 'https://en.wikipedia.org/wiki/Scarlett_Johansson',
    bio: { es: 'Actriz y productora estadounidense. Lost in Translation, Match Point, Her, Black Widow, Marriage Story.', en: 'American actress & producer. Lost in Translation, Match Point, Her, Black Widow, Marriage Story.' },
    sample: { es: 'Hola... soy Rachel. Ven conmigo... déjame cuidar de tu restaurante esta noche.', en: "Hi... I'm Rachel. Come with me... let me take care of your restaurant tonight." } },
  { id: 'sage', label: 'Natalie P.', es: 'Serena · Precisa · Ejecutiva', en: 'Calm · Precise · Executive', img: '/avatars/wiki-natalie.jpg',
    wiki: 'https://en.wikipedia.org/wiki/Natalie_Portman',
    bio: { es: 'Actriz y directora israelí-estadounidense, Óscar por Cisne Negro. Léon, V de Vendetta, Jackie.', en: 'Israeli-American actress & director, Oscar for Black Swan. Léon, V for Vendetta, Jackie.' },
    sample: { es: 'Hola. Soy Rachel. Respira... y déjame mostrarte tu restaurante como nunca lo habías visto.', en: "Hello. I'm Rachel. Breathe... and let me show you your restaurant like you've never seen it." } },
  { id: 'nova', label: 'Jennifer L.', es: 'Latina · Vibrante · Magnética', en: 'Latin · Vibrant · Magnetic', img: '/avatars/wiki-jennifer.jpg',
    wiki: 'https://en.wikipedia.org/wiki/Jennifer_Lopez',
    bio: { es: 'Cantante, actriz y empresaria de raíces puertorriqueñas. Selena, The Wedding Planner, Hustlers.', en: 'Singer, actress & entrepreneur with Puerto Rican roots. Selena, The Wedding Planner, Hustlers.' },
    sample: { es: 'Hola, gracias por estar... y deja que te enseñe hasta dónde puede llegar tu restaurante.', en: "Hi, thank you for being here... let me show you how far your restaurant can go." } },
  { id: 'onyx', label: 'Alejandro F.', es: 'Profundo · Carismático · El mero mero', en: 'Deep · Charismatic · The man of men', img: '/avatars/wiki-alejandro.jpg',
    wiki: 'https://en.wikipedia.org/wiki/Alejandro_Fern%C3%A1ndez',
    bio: { es: 'Cantante mexicano, "El Potrillo". Ícono de la música ranchera y latina, más de 20 millones de discos.', en: 'Mexican singer, "El Potrillo". Ranchera and Latin music icon, over 20 million records sold.' },
    sample: { es: 'Amigo... Kitxens es de lo mejor que le puede pasar a tu restaurante. Palabra del mero mero.', en: "My friend... Kitxens is the best thing that can happen to your restaurant. Word of the man of men." } },
];
export function VoicePicker({ isEs, current, onPick }) {
  const [playing, setPlaying] = useState('');
  const audioRef = useRef(null);
  const seqRef = useRef(0);
  const lang = isEs ? 'es' : 'en';
  const rachelImg = (TEAM_AVATARS.find((a) => a.key === 'rachel') || {}).img;

  // The score waits — it enters only once they choose Rachel's voice.
  // Pre-warm the intro narration so the show starts the instant they pick — no dead air.
  useEffect(() => {
    fetchVoiceUrl(introText(lang), lang, current).catch(() => {});
  }, [lang, current]);
  // Pre-warm every voice's "listen" sample so ▶ plays instantly — staggered, never a burst.
  useEffect(() => {
    const timers = VOICES.map((v, i) => setTimeout(() => fetchVoiceUrl(v.sample[lang], lang, v.id).catch(() => {}), 150 + i * 220));
    return () => timers.forEach(clearTimeout);
  }, [lang]);

  const preview = async (e, id) => {
    e.stopPropagation();
    const seq = ++seqRef.current; // only the LAST requested preview may play
    stopAllVoices();
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (playing === id) { setPlaying(''); return; }
    setPlaying(`loading-${id}`);
    fetchVoiceUrl(introText(lang), lang, id).catch(() => {}); // pre-warm this voice's intro too
    try {
      const ep = getVoiceEpoch();
      const vDef = VOICES.find((v) => v.id === id);
      const url = await fetchVoiceUrl(vDef.sample[lang], lang, id);
      if (seq !== seqRef.current || ep !== getVoiceEpoch()) return; // a newer click or an exit won — discard
      const { audio, done } = playUrl(url, 1);
      audioRef.current = audio;
      setPlaying(id);
      done.then(() => { setPlaying((p) => (p === id ? '' : p)); });
    } catch { setPlaying(''); }
  };

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center px-6 overflow-y-auto" data-testid="present-voice-picker"
      style={{ background: 'radial-gradient(ellipse at center, #16171a 0%, #060607 78%)' }}>
      <div className="max-w-3xl w-full text-center my-auto py-10" style={{ animation: 'kx-intro-line 1.2s cubic-bezier(0.16,1,0.3,1) both' }}>
        {rachelImg && <img src={rachelImg} alt="Rachel" data-testid="picker-rachel-avatar"
          className="w-20 h-20 rounded-full object-cover mx-auto border-2 border-[#F77F11]/50 shadow-[0_0_30px_rgba(247,127,17,0.25)]" />}
        <p className="mt-4 text-[10px] uppercase tracking-[0.5em] font-bold text-[#F77F11]">KITXENS · RESTAURANT AI OS</p>
        <h2 className="mt-4 text-3xl sm:text-4xl text-white leading-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
          {isEs ? '¿Con qué voz quieres que Rachel te acompañe?' : 'Which voice should Rachel use for your tour?'}
        </h2>
        <p className="mt-2 text-[13px] text-white/45">{isEs ? 'Voces inspiradas en grandes artistas — todas recomiendan Kitxens. Elige la tuya.' : 'Voices inspired by great artists — every one of them recommends Kitxens. Pick yours.'}</p>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-stretch">
          {VOICES.map((v, i) => (
            <button key={v.id} onClick={() => onPick(v.id)} data-testid={`voice-pick-${v.id}`}
              className={`group relative flex flex-col rounded-3xl border px-5 py-6 text-left transition-all duration-300 hover:-translate-y-1 ${current === v.id ? 'border-[#F77F11] bg-[#F77F11]/10' : 'border-white/12 bg-white/[0.03] hover:border-white/35'}`}
              style={{ animation: `kx-intro-line 1s ${0.25 + i * 0.15}s cubic-bezier(0.16,1,0.3,1) both` }}>
              <img src={v.img} alt={v.label} className="w-20 h-20 rounded-full object-cover mx-auto border-2 border-white/15 group-hover:border-[#F77F11]/60 transition-colors shrink-0" style={{ objectPosition: 'top' }} />
              <p className="mt-3 text-[19px] leading-none whitespace-nowrap text-white text-center" style={{ fontFamily: 'Georgia, serif' }}>{v.label}</p>
              <p className="mt-2 text-[12px] text-white/50 text-center min-h-[54px]">{isEs ? v.es : v.en}</p>
              <a href={v.wiki} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                data-testid={`voice-wiki-${v.id}`}
                className="mt-1 block text-center text-[9.5px] uppercase tracking-[0.18em] text-white/30 hover:text-[#F77F11] transition-colors">
                {isEs ? 'Foto: Wikipedia ↗' : 'Photo: Wikipedia ↗'}
              </a>
              <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-3 -translate-y-full w-60 rounded-xl border border-white/15 bg-[#101114]/95 p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
                data-testid={`voice-bio-${v.id}`} style={{ backdropFilter: 'blur(10px)' }}>
                <p className="text-[11.5px] text-white/75 leading-relaxed">{isEs ? v.bio.es : v.bio.en}</p>
              </div>
              <span onClick={(e) => preview(e, v.id)} role="button" tabIndex={0} data-testid={`voice-preview-${v.id}`}
                onKeyDown={(e) => e.key === 'Enter' && preview(e, v.id)}
                className="mt-auto pt-4 flex justify-center items-center gap-1.5 text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#F77F11] hover:text-white transition-colors">
                {playing === `loading-${v.id}` ? (isEs ? 'Cargando…' : 'Loading…') : playing === v.id ? (isEs ? '◼ Parar' : '◼ Stop') : (isEs ? '▶ Escuchar' : '▶ Listen')}
              </span>
            </button>
          ))}
        </div>
        <button onClick={() => onPick(null)} data-testid="voice-pick-none"
          className="mt-7 text-[11px] uppercase tracking-[0.25em] font-bold text-white/35 hover:text-white/70 transition-colors">
          {isEs ? 'Continuar sin voz' : 'Continue without voice'}
        </button>
      </div>
      <style>{`@keyframes kx-intro-line {
        0% { opacity: 0; transform: translateY(26px) scale(0.97); filter: blur(8px); }
        100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
      }`}</style>
    </div>
  );
}

function PartnerInvite({ isEs }) {
  const [pEmail, setPEmail] = useState('');
  const [st, setSt] = useState('idle'); // idle | sending | done | partner | error
  const [aff, setAff] = useState(null);
  // Partners only: restaurant guests (clients & prospects) never see the partner pitch.
  const [isPartner, setIsPartner] = useState(false);
  useEffect(() => {
    fetch(`${API}/api/affiliates/me`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.affiliate) { setAff(d.affiliate); setSt('partner'); setIsPartner(true); } })
      .catch(() => {});
  }, []);
  const go = async () => {
    const e = pEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { setSt('error'); return; }
    setSt('sending');
    try {
      const r = await fetch(`${API}/api/demo-access/partner-interest`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: e, locale: isEs ? 'es' : 'en' }) });
      if (!r.ok) throw new Error();
      const d = await r.json();
      if (d.already_partner) { setAff(d); setSt('partner'); } else setSt('done');
    } catch { setSt('error'); }
  };
  if (!isPartner) return null;
  return (
    <div className="mt-9 mx-auto max-w-xl rounded-2xl border border-white/12 px-5 py-5 text-center"
      style={{ background: 'rgba(255,255,255,0.04)' }} data-testid="finale-partner-invite">
      {st === 'partner' && aff ? (
        <div data-testid="finale-partner-known">
          <p className="text-[14px] font-bold text-white">
            {isEs ? `¡Bienvenido de nuevo${aff.name ? `, ${aff.name}` : ''}! Ya eres de la casa.` : `Welcome back${aff.name ? `, ${aff.name}` : ''}! You're already family.`}
          </p>
          {aff.approved && aff.ref_code ? (
            <p className="mt-2 text-[13px] text-white/70">
              {isEs ? 'Tu código de siempre: ' : 'Your usual code: '}
              <span className="font-mono font-bold text-[#F77F11]" data-testid="finale-partner-code">{aff.ref_code}</span>
            </p>
          ) : (
            <p className="mt-2 text-[13px] text-white/70">{isEs ? 'Tu solicitud está en revisión — te avisaremos pronto.' : 'Your application is under review — we will let you know soon.'}</p>
          )}
          <a href="/affiliates/dashboard" target="_blank" rel="noreferrer" data-testid="finale-partner-dashboard"
            className="mt-3 inline-block rounded-full border border-[#F77F11]/60 text-[#F77F11] text-[12.5px] font-bold px-5 py-2 hover:bg-[#F77F11]/10 transition-colors">
            {isEs ? 'Ir a mi panel de partner →' : 'Go to my partner dashboard →'}
          </a>
        </div>
      ) : st === 'done' ? (
        <div data-testid="finale-partner-done">
          <p className="text-[14px] font-bold text-white">{isEs ? 'Hecho — Eva ya sabe que quieres ser partner.' : 'Done — Eva knows you want to be a partner.'}</p>
          <p className="mt-1.5 text-[12.5px] text-white/60">
            {isEs ? 'Completa tu alta en un minuto y recibe tu código para empezar a ganar.' : 'Complete your signup in a minute and get your code to start earning.'}
          </p>
          <a href="/affiliates" target="_blank" rel="noreferrer" data-testid="finale-partner-signup"
            className="mt-3 inline-block rounded-full bg-[#F77F11] text-white text-[12.5px] font-bold px-5 py-2 hover:brightness-110 transition-[filter]">
            {isEs ? 'Completar mi alta de partner →' : 'Complete my partner signup →'}
          </a>
        </div>
      ) : (
        <>
          <p className="text-[10px] uppercase tracking-[0.35em] font-bold text-[#F77F11]">
            {isEs ? '¿CONOCES MÁS RESTAURANTES?' : 'KNOW MORE RESTAURANTS?'}
          </p>
          <p className="mt-2 text-[13.5px] text-white/70 leading-relaxed">
            {isEs ? 'Hazte partner de KITXENS: recomienda y gana 30% recurrente por cada restaurante que traigas.'
              : 'Become a KITXENS partner: refer and earn 30% recurring for every restaurant you bring.'}
          </p>
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-2.5">
            <input type="email" value={pEmail} onChange={(e) => { setPEmail(e.target.value); if (st === 'error') setSt('idle'); }}
              placeholder={isEs ? 'tu@email.com' : 'you@email.com'} data-testid="finale-partner-email"
              className={`w-full sm:w-60 rounded-full bg-white/[0.06] border px-4 py-2.5 text-[13px] text-white placeholder-white/30 outline-none focus:border-[#F77F11]/70 transition-colors ${st === 'error' ? 'border-red-500/70' : 'border-white/15'}`} />
            <button onClick={go} disabled={st === 'sending'} data-testid="finale-partner-cta"
              className="rounded-full border border-[#F77F11]/70 text-[#F77F11] text-[12.5px] font-bold px-5 py-2.5 hover:bg-[#F77F11]/10 transition-colors disabled:opacity-60">
              {st === 'sending' ? (isEs ? 'Un segundo…' : 'One second…') : (isEs ? 'Quiero ser partner' : 'I want to be a partner')}
            </button>
          </div>
          {st === 'error' && (
            <p className="mt-2 text-[11.5px] text-red-400/90" data-testid="finale-partner-error">
              {isEs ? 'Revisa el email — parece incompleto.' : 'Check the email — it looks incomplete.'}
            </p>
          )}
        </>
      )}
    </div>
  );
}

export function FinaleScreen({ isEs, voiceOn = true, voice = 'shimmer', onExplore, onFallback }) {
  const v = VOICES.find((x) => x.id === voice) || VOICES[0];
  const [email, setEmail] = useState(() => { try { return localStorage.getItem('kx_welcome_email') || ''; } catch { return ''; } });
  const [status, setStatus] = useState('idle'); // idle | sending | done | error

  useEffect(() => {
    startTheme();
    setThemeBase(0.2); // the score swells for the closing scene
    const soften = setTimeout(() => setThemeBase(0.09), 18000); // then settles into a soft bed while they think — never an abrupt cut
    let narration = null;
    let alive = true;
    const ep = getVoiceEpoch();
    if (voiceOn) {
      const gn = getGuestName();
      const text = isEs
        ? `Hemos recorrido tus ingresos, tu menú, tus reseñas... y las oportunidades que tu equipo de inteligencia artificial encontró. Y recuerda${gn ? `, ${gn}` : ''}: esto ha sido solo una demostración. Imagina lo que Kitxens podría descubrir sobre tu propio restaurante. Déjame tu email... y lo descubrimos juntos.`
        : `We walked through your revenue, your menu, your reviews... and the opportunities your AI team found. And remember${gn ? `, ${gn}` : ''}: this was only a demonstration. Imagine what Kitxens could discover about your own restaurant. Leave me your email... and we'll find out together.`;
      fetchVoiceUrl(text, isEs ? 'es' : 'en', voice)
        .then((url) => { if (alive && ep === getVoiceEpoch()) narration = playUrl(url, 1).audio; })
        .catch(() => {});
    }
    return () => { alive = false; clearTimeout(soften); if (narration) narration.pause(); };
  }, [isEs, voiceOn, voice]);

  const submit = async () => {
    const e = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { setStatus('error'); return; }
    setStatus('sending');
    try {
      const r = await fetch(`${API}/api/demo-access/profile`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_me: true, contact_email: e, ref: getRef() || '' }),
      });
      if (!r.ok) throw new Error('no_session');
      setStatus('done');
      stopTheme(true); // curtain falls softly
    } catch { if (onFallback) onFallback(); }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center px-6 overflow-y-auto" data-testid="present-finale"
      style={{ background: 'radial-gradient(ellipse at center, #16171a 0%, #060607 78%)' }}>
      <button onClick={onExplore} data-testid="finale-close" aria-label={isEs ? 'Cerrar demo' : 'Close demo'}
        title={isEs ? 'Cerrar y explorar por tu cuenta' : 'Close and explore on your own'}
        className="absolute top-5 right-5 w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors">
        <X className="w-5 h-5" />
      </button>
      <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: 'radial-gradient(ellipse 60% 38% at 50% 58%, rgba(247,127,17,0.09), transparent 70%)' }} />
      <div className="max-w-3xl w-full text-center py-10 my-auto" style={{ animation: 'kx-intro-line 1.3s cubic-bezier(0.16,1,0.3,1) both' }}>
        <img src={v.img} alt={v.label} className="w-24 h-24 rounded-full object-cover mx-auto border-2 border-[#F77F11]/60 shadow-[0_0_40px_rgba(247,127,17,0.35)]" />
        <p className="mt-5 text-[10px] uppercase tracking-[0.5em] font-bold text-[#F77F11]">{isEs ? 'RACHEL · AI CONCIERGE DE KITXENS' : 'RACHEL · KITXENS AI CONCIERGE'}</p>
        <a href={v.wiki} target="_blank" rel="noreferrer" data-testid="finale-voice-credit"
          className="mt-1.5 inline-block text-[11px] text-white/45 hover:text-[#F77F11] transition-colors underline-offset-2 hover:underline">
          {isEs ? `Con la voz al estilo de ${v.label} · Wikipedia ↗` : `Voice in the style of ${v.label} · Wikipedia ↗`}
        </a>
        {status === 'done' ? (
          <div data-testid="finale-success">
            <h2 className="mt-4 text-3xl sm:text-5xl text-white leading-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
              {isEs ? 'Hecho. El equipo ya está en marcha.' : 'Done. The team is already moving.'}
            </h2>
            <p className="mt-3 text-[15px] text-white/55" style={{ fontFamily: 'Georgia, serif' }}>
              {isEs ? 'Eva y el AI Team acaban de recibir el aviso — Rachel te escribirá muy pronto.' : 'Eva and the AI Team just got the word — Rachel will write to you very soon.'}
            </p>
            <button onClick={onExplore} data-testid="finale-cta-explore"
              className="mt-9 text-[11px] uppercase tracking-[0.25em] font-bold text-white/50 hover:text-white transition-colors">
              {isEs ? 'Seguir explorando el OS' : 'Keep exploring the OS'}
            </button>
          </div>
        ) : (
          <>
            <h2 className="mt-4 text-3xl sm:text-5xl text-white leading-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
              {isEs ? 'Esto es solo el principio.' : 'This is just the beginning.'}
            </h2>
            <p className="mt-3 text-[15px] text-white/55" style={{ fontFamily: 'Georgia, serif' }}>
              {isEs ? 'Deja tu email — Rachel, nuestra AI Concierge, toma tus datos y Eva, nuestra COO, pone al equipo en marcha por tu restaurante.' : 'Leave your email — Rachel, our AI Concierge, takes your details and Eva, our COO, gets the team moving for your restaurant.'}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); if (status === 'error') setStatus('idle'); }}
                placeholder={isEs ? 'tu@email.com' : 'you@email.com'} data-testid="finale-email-input"
                className={`w-full sm:w-72 rounded-full bg-white/[0.06] border px-5 py-3.5 text-[14px] text-white placeholder-white/30 outline-none focus:border-[#F77F11]/70 transition-colors ${status === 'error' ? 'border-red-500/70' : 'border-white/15'}`} />
              <button onClick={submit} disabled={status === 'sending'} data-testid="finale-cta-send"
                className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-[14px] font-bold text-white disabled:opacity-60"
                style={{ background: '#F77F11', animation: 'kx-begin-glow 2.6s ease-in-out infinite' }}>
                {status === 'sending' ? (isEs ? 'Enviando…' : 'Sending…') : (isEs ? 'Quiero que me contacten' : 'Have Rachel contact me')}
              </button>
            </div>
            {status === 'error' && (
              <p className="mt-3 text-[12px] text-red-400/90" data-testid="finale-email-error">{isEs ? 'Revisa el email — parece incompleto.' : 'Check the email — it looks incomplete.'}</p>
            )}
            <button onClick={onExplore} data-testid="finale-cta-explore"
              className="mt-7 text-[11px] uppercase tracking-[0.25em] font-bold text-white/40 hover:text-white/80 transition-colors">
              {isEs ? 'Seguir explorando el OS' : 'Keep exploring the OS'}
            </button>
          </>
        )}
        <PartnerInvite isEs={isEs} />
      </div>
      <style>{`
        @keyframes kx-intro-line { 0% { opacity: 0; transform: translateY(26px) scale(0.97); filter: blur(8px); } 100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); } }
        @keyframes kx-begin-glow { 0%, 100% { box-shadow: 0 0 0 rgba(247,127,17,0.0); transform: scale(1); } 50% { box-shadow: 0 0 34px rgba(247,127,17,0.55); transform: scale(1.03); } }
      `}</style>
    </div>
  );
}

export function CinematicIntro({ isEs, voiceOn = true, voice = 'shimmer', onDone }) {
  const [step, setStep] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const [narrDone, setNarrDone] = useState(!voiceOn);
  const [ready, setReady] = useState(!voiceOn); // lines wait for Rachel's actual voice — true sync
  const [scale, setScale] = useState(1); // line pacing follows the REAL narration length
  const lines = INTRO_LINES[isEs ? 'es' : 'en'];
  const soundRef = useRef({ music: null, narration: null });

  useEffect(() => {
    startTheme(); // the score enters now — they chose
    setThemeBase(0.17); // rises slowly, always under Rachel
    let alive = true;
    let failsafe; let readyFail;
    if (voiceOn) {
      failsafe = setTimeout(() => setNarrDone(true), 26000);
      readyFail = setTimeout(() => setReady(true), 4000);
      fetchVoiceUrl(introText(isEs ? 'es' : 'en'), isEs ? 'es' : 'en', voice)
        .then((url) => {
          if (!alive) return;
          const { audio, done } = playUrl(url, 1); // auto-ducks the score
          soundRef.current.narration = audio;
          audio.addEventListener('playing', () => {
            setReady(true);
            // Stretch/compress line timing so the last line lands with her last words.
            const d = (audio.duration || 0) * 1000;
            const total = LINE_MS.reduce((a, b) => a + b, 0);
            if (d > 5000) setScale(Math.max(0.7, Math.min(1.8, (d - 1200) / total)));
          }, { once: true });
          done.then(() => setNarrDone(true));
        }).catch(() => { setNarrDone(true); setReady(true); });
    }
    return () => {
      alive = false;
      if (failsafe) clearTimeout(failsafe);
      if (readyFail) clearTimeout(readyFail);
      const { narration: n } = soundRef.current;
      if (n) { n.pause(); }
      soundRef.current = { music: null, narration: null };
      setThemeBase(0.11); // settles into a discreet bed under the live tour
    };
  }, [isEs, voiceOn, voice]);

  useEffect(() => {
    if (!ready || step >= lines.length) return undefined; // hold last frame until narration ends
    const t = setTimeout(() => setStep((s) => s + 1), Math.round((LINE_MS[step] || 1600) * scale));
    return () => clearTimeout(t);
  }, [ready, step, lines.length, scale]);

  const doneFired = useRef(false);
  useEffect(() => {
    if (step >= lines.length && narrDone && !doneFired.current) {
      doneFired.current = true; // fire exactly once — leaving re-render must never cancel the handoff
      setLeaving(true);
      setTimeout(onDone, 1100);
    }
  }, [step, narrDone, lines.length, onDone]);

  const skip = () => { setLeaving(true); setTimeout(onDone, 400); };
  const shown = Math.min(step, lines.length - 1);
  const last = shown === lines.length - 1;

  return (
    <div onClick={skip} data-testid="present-cinematic-intro"
      className={`fixed inset-0 z-[95] flex items-center justify-center cursor-pointer transition-opacity duration-700 ${leaving ? 'opacity-0' : 'opacity-100'}`}
      style={{ background: 'radial-gradient(ellipse at center, #16171a 0%, #060607 78%)' }}>
      <div className="pointer-events-none absolute inset-0" style={{ boxShadow: 'inset 0 0 220px rgba(0,0,0,0.85)' }} />
      <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: 'radial-gradient(ellipse 60% 38% at 50% 58%, rgba(247,127,17,0.09), transparent 70%)', animation: 'kx-intro-glow 12s ease-in-out both' }} />
      <div className="px-8 text-center max-w-4xl">
        <p key={shown} data-testid="present-intro-line"
          className={`text-white ${last ? 'text-3xl sm:text-5xl' : 'text-4xl sm:text-6xl'} leading-tight`}
          style={{ fontFamily: 'Georgia, "Times New Roman", serif', letterSpacing: '0.01em', animation: 'kx-intro-line 1.6s cubic-bezier(0.16,1,0.3,1) both' }}>
          {lines[shown]}
        </p>
        {last && (
          <p className="mt-8 text-[11px] uppercase tracking-[0.5em] font-bold text-[#F77F11]"
            style={{ animation: 'kx-intro-line 1.8s 0.7s cubic-bezier(0.16,1,0.3,1) both' }}>
            KITXENS · RESTAURANT AI OS
          </p>
        )}
      </div>
      <span className="absolute bottom-8 right-8 text-[11px] uppercase tracking-[0.25em] text-white/35 font-bold">
        {isEs ? 'Toca para saltar' : 'Tap to skip'}
      </span>
      <style>{`@keyframes kx-intro-line {
        0% { opacity: 0; transform: translateY(26px) scale(0.97); filter: blur(8px); }
        100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
      }
      @keyframes kx-intro-glow { 0% { opacity: 0; } 40% { opacity: 0.6; } 100% { opacity: 0.25; } }`}</style>
    </div>
  );
}

/* Each step: sel (CSS selector), es/en (what the presenter says), click (really click it).
   Voice: a trusted advisor — calm, elegant, human. Every scene lands a discovery. */
const SCRIPTS = {
  '': [
    { sel: '[data-testid="rest-hero-dish"], [data-testid="rest-welcome-brief"]',
      es: 'Bienvenido a {rest}. Cada mañana, este informe te espera con tu plato del día y lo esencial de ayer... sin hojas de cálculo. A los propietarios les encanta empezar el día aquí.',
      en: 'Welcome to {rest}. Every morning this brief is waiting for you, with your dish of the day and the essentials from yesterday... no spreadsheets. Owners love starting their day here.' },
    { sel: '[data-testid="rest-brief-q1"]',
      es: 'Fíjate en esto: las tres preguntas que todo propietario se hace al despertar, ya respondidas. Cómo fue ayer, qué ocurre hoy... y cuál es la siguiente acción.',
      en: 'Notice this: the three questions every owner asks when they wake up, already answered. How was yesterday, what is happening today... and what the next move is.' },
    { sel: '[data-testid="rest-team-concierge"]',
      es: 'Y este es tu equipo de inteligencia artificial, trabajando mientras duermes. Habla de tu negocio, con tus números. Fíjate en cómo todo está conectado.',
      en: 'And this is your AI team, working while you sleep. It talks about your business, with your numbers. Notice how everything is connected.' },
  ],
  'intelligence': [
    { sel: '[data-testid="intel-range-90"]', click: true,
      es: 'Esta es una de mis pantallas favoritas. Voy a ampliar la vista a noventa días... la tendencia cuenta una historia que un solo día no puede contar.',
      en: 'This is one of my favorite screens. Let me widen the view to ninety days... the trend tells a story a single day never could.' },
    { sel: '[data-testid="funnel-revenue"]', click: true,
      es: 'Ahora abro los ingresos estimados... y aparece el desglose por canal. Aquí es donde muchos propietarios descubren, por primera vez, de dónde viene realmente su dinero.',
      en: 'Now I open estimated revenue... and the channel breakdown appears. This is where many owners discover, for the first time, where their money really comes from.' },
    { sel: '[data-testid="intel-breakdown"]',
      es: 'Google, Instagram, incluso ChatGPT... cada visita queda atribuida a su origen. Hasta hace muy poco, esto solo lo tenían las grandes cadenas.',
      en: 'Google, Instagram, even ChatGPT... every single visit gets attributed to its source. Until very recently, only the big chains had this.' },
  ],
  'revenue': [
    { sel: '[data-testid="rest-rev-mix"]',
      es: 'Aquí hay un descubrimiento interesante: cuánto ingresa tu sala, cuánto tu propia web... y cuánto se queda el delivery de terceros con su comisión.',
      en: 'Here is an interesting discovery: how much your dining room brings in, how much your own website... and how much third-party delivery keeps in commissions.' },
    { sel: '[data-testid="rev-view-mix"]', click: true,
      es: 'Cambio la vista al mix de canales... y ahora puedes ver, día a día, de dónde vino cada dólar. Tómate tu tiempo con esta.',
      en: 'I switch the chart to channel mix... and now you can see, day by day, where every dollar came from. Take your time with this one.' },
    { sel: '[data-testid="rev-breakdown-table"]',
      es: 'Y aquí abajo, cada periodo con y sin Kitxens. Impacto medible, no promesas. Esto es lo que hace diferente a Kitxens.',
      en: 'And down here, every period with and without Kitxens. Measurable impact, not promises. This is what makes Kitxens different.' },
  ],
  'locations': [
    { sel: '[data-testid="rest-locations-map"]',
      es: 'Aquí está el grupo completo en un solo mapa: cada local con su cuota de ventas, de Nueva York a Connecticut. Un restaurante multi-local ya no se dirige por teléfono... se dirige desde aquí.',
      en: 'Here is the whole group on one map: every location with its sales share, from New York to Connecticut. A multi-location restaurant is no longer run by phone calls... it is run from here.' },
    { sel: '[data-testid="rest-locations-rank-1"]', click: true,
      es: 'El ranking muestra quién lidera y quién necesita ayuda hoy. Toco el primero... y su detalle se abre con ventas, ticket medio y plato estrella.',
      en: 'The ranking shows who leads and who needs help today. I tap the first one... and its detail opens with sales, average ticket and star dish.' },
    { sel: '[data-testid="rest-locations-compare"]',
      es: 'Y mi favorito: el comparador. Dos locales cara a cara, con el ganador de cada métrica resaltado. El Efecto Kitxens también se calcula local por local — cada casa tiene su propio compromiso.',
      en: 'And my favorite: the comparator. Two locations head to head, with each metric\u2019s winner highlighted. The Kitxens Effect is also computed per location — every house gets its own commitment.' },
  ],
  'menu': [
    { sel: '[data-testid="menu-popular-dishes"]',
      es: 'Tus platos más vendidos, con fotos y cifras reales de tus propios reportes. Mira cómo rinde tu {dish}...{vibe} los propietarios suelen sorprenderse con lo que descubren en esta pantalla.',
      en: 'Your best-selling dishes, with photos and real numbers from your own reports. Look at how your {dish} performs...{vibe} owners are usually surprised by what they discover on this screen.' },
    { sel: '[data-testid="menu-quadrant-star"]', click: true,
      es: 'Toco el filtro Estrella... alta rotación y alto margen. Platos como tu {dish2} son los que merecen protagonismo esta semana.',
      en: 'I tap the Star filter... high sales and high margin. Dishes like your {dish2} are the ones that deserve the spotlight this week.' },
    { sel: '[data-testid="menu-class-help"]', click: true,
      es: 'Y si algún término suena raro, un clic y lo explicamos en lenguaje de restaurante. Sin jerga de consultoría.',
      en: 'And if any term sounds odd, one click and we explain it in restaurant language. No consultant jargon.' },
  ],
  'reviews': [
    { sel: '[data-testid="reviews-platforms"]',
      es: 'Todas tus reseñas de Google, TripAdvisor y Yelp, reunidas en un solo lugar. Se acabó saltar entre pestañas.',
      en: 'All your Google, TripAdvisor and Yelp reviews, gathered in one place. No more jumping between tabs.' },
    { sel: '[data-testid^="review-suggestion-"]',
      es: 'Y esta es una de mis funciones favoritas: la respuesta ya está redactada. Tú solo la apruebas... o activas el piloto automático.',
      en: 'And this is one of my favorite features: the reply is already written. You just approve it... or turn on autopilot.' },
  ],
  'ai-executive': [
    { sel: '[data-testid="roi-multiple"]',
      es: 'Esta cifra lo resume todo: el retorno de cada dólar invertido en Kitxens. Deja que hable por sí sola.',
      en: 'This number sums it all up: the return on every dollar invested in Kitxens. Let it speak for itself.' },
    { sel: '[data-testid^="opp-"]',
      es: 'Tu ejecutivo de inteligencia artificial ya encontró oportunidades concretas para esta semana — como impulsar tu {dish} en horas pico — cada una con su impacto estimado. Sin informes interminables.',
      en: 'Your AI executive already found concrete opportunities for this week — like pushing your {dish} at peak hours — each with its estimated impact. No endless reports.' },
    { sel: '[data-testid^="action-"]', click: true,
      es: 'Y las convierte en una lista de acciones clara. Marco una como completada... así de sereno se siente dirigir un restaurante con datos.',
      en: 'And it turns them into a clear action list. I check one off... this is how calm running a restaurant on data feels.' },
  ],
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
async function waitFor(sel, cancelled, tries = 12) {
  for (let i = 0; i < tries; i += 1) {
    if (cancelled.current) return null;
    const el = document.querySelector(sel);
    if (el && el.getBoundingClientRect().width > 0) return el;
    await sleep(400);
  }
  return null;
}

const CAMEOS = {
  '': { who: 'eva', es: 'Si el informe está en verde, la cocina fluye. Lo reviso cada mañana a las ocho.', en: 'If the brief is green, the kitchen flows. I check it every morning at 8.' },
  'intelligence': { who: 'stan', es: 'Noventa días de tendencia… aquí es donde identifico a tus próximos clientes.', en: 'Ninety days of trend… this is where I find your next customers.' },
  'revenue': { who: 'penny', es: 'Ese mix de canales es lo primero que analizo cada lunes — ahí se encuentra el margen.', en: 'That channel mix is the first thing I check every Monday — the margin hides there.' },
  'menu': { who: 'sonny', es: 'Ese plato Estrella merece un reel. Yo lo publicaría el jueves a las seis de la tarde.', en: 'That Star dish is begging for a reel. I would post it Thursday at 6pm.' },
  'reviews': { who: 'sonny', es: 'Responder en menos de veinticuatro horas eleva tu calificación media — y Rachel ya lo hace por ti.', en: 'Replying within 24h lifts your average rating — and Rachel already does it for you.' },
  'locations': { who: 'eva', es: 'Si un local cae, lo vemos el mismo día — no al final del mes. Así se dirige un grupo.', en: 'If one location dips, we see it the same day — not at month end. That is how you run a group.' },
  'ai-executive': { who: 'eva', es: 'Lista de acciones clara, equipo alineado. Así se dirige una operación con serenidad.', en: 'Clear checklist, aligned team. That is how you run an operation without shouting.' },
};

function TeamCameo({ viewKey, runId, isEs, active }) {
  const [show, setShow] = useState(false);
  const cameo = CAMEOS[viewKey];
  const av = cameo ? TEAM_AVATARS.find((a) => a.key === cameo.who) : null;
  useEffect(() => {
    setShow(false);
    if (!active || !cameo) return undefined;
    const t1 = setTimeout(() => setShow(true), 8000);
    const t2 = setTimeout(() => setShow(false), 17000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [viewKey, runId, active, cameo]);
  if (!show || !av) return null;
  return (
    <div className="fixed left-6 bottom-24 z-[86] max-w-[300px] max-sm:left-1/2 max-sm:-translate-x-1/2 max-sm:w-[92vw] max-sm:max-w-[92vw] max-sm:bottom-auto max-sm:top-16" data-testid="present-team-cameo">
      {/* Animation lives on the INNER card — never on the positioned element (transform conflict). */}
      <div className="flex items-start gap-3 rounded-2xl px-4 py-3 shadow-2xl border border-white/15"
        style={{ background: 'rgba(18,19,22,0.93)', backdropFilter: 'blur(16px)', animation: 'kx-cameo-in 0.8s cubic-bezier(0.16,1,0.3,1) both' }}>
        <img src={av.img} alt={av.name} className="w-9 h-9 rounded-full object-cover border border-white/20 shrink-0" />
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50">
            <span className="text-[#F77F11]">{av.name}</span> · {av.role} · {isEs ? 'viendo la demo' : 'watching along'}
          </p>
          <p className="mt-1 text-[12.5px] leading-relaxed text-white/90" style={{ fontFamily: 'Georgia, serif' }}>
            {isEs ? cameo.es : cameo.en}
          </p>
        </div>
      </div>
      <style>{'@keyframes kx-cameo-in { 0% { opacity: 0; transform: translateX(-30px); } 100% { opacity: 1; transform: translateX(0); } }'}</style>
    </div>
  );
}

export function PresentDirector({ active, isEs, viewKey, runId, voiceOn = true, voice = 'shimmer', navTo, onDone, demoSlug = '' }) {
  const [cursor, setCursor] = useState({ x: -100, y: -100, visible: false, clicking: false });
  const [caption, setCaption] = useState(null);
  const speakingRef = useRef(null);
  const resumeRef = useRef({ key: '', i: 0, seen: '' }); // remembers WHERE we are — voice/lang switches resume, never restart
  const voiceRef = useRef(voice); // voice changes apply on the NEXT line — the current one never cuts off
  useEffect(() => { voiceRef.current = voice; }, [voice]);

  // Guest takes the wheel: the ghost cursor steps aside while they drive — the show keeps rolling.
  const userTsRef = useRef(0);
  useEffect(() => {
    const onUser = () => { userTsRef.current = Date.now(); setCursor((c) => (c.visible ? { ...c, visible: false } : c)); };
    window.addEventListener('kx-user-control', onUser);
    return () => window.removeEventListener('kx-user-control', onUser);
  }, []);
  const ghostOk = () => Date.now() - userTsRef.current > 6000;

  useEffect(() => {
    if (!active) return undefined;
    // Per-run token: once this run is cancelled it STAYS cancelled — a newer run can never resurrect
    // an old loop (the old shared flag caused two scripts to narrate at once after a lang/voice switch).
    const token = { current: false };
    const script = (SCRIPTS[viewKey] || []).map((s) => ({
      ...s, es: contextualize(s.es, demoSlug, 'es'), en: contextualize(s.en, demoSlug, 'en') }));
    const lang = isEs ? 'es' : 'en';
    const guestName = getGuestName();
    const posKey = `${viewKey}|${runId}`;
    if (resumeRef.current.key !== posKey) resumeRef.current = { key: posKey, i: 0, seen: '', lang };
    // A language flip re-reads the scene from its very first line — the story is never lost mid-sentence.
    if (resumeRef.current.lang !== lang) resumeRef.current = { key: posKey, i: 0, seen: '', lang };

    // Human presence: the ghost cursor drifts subtly now and then, like a real hand on a mouse.
    const wander = setInterval(() => {
      setCursor((c) => (c.visible && !c.clicking
        ? { ...c,
            x: Math.max(30, Math.min(window.innerWidth - 40, c.x + (Math.random() * 44 - 22))),
            y: Math.max(30, Math.min(window.innerHeight - 130, c.y + (Math.random() * 30 - 15))) }
        : c));
    }, 3200);

    (async () => {
      await sleep(resumeRef.current.i > 0 ? 400 : 1400);
      if (voiceOn) script.forEach((s) => fetchVoiceUrl(personalize(isEs ? s.es : s.en, guestName), lang, voiceRef.current).catch(() => {}));

      // PHASE A — narrative sync: the director REALLY clicks the menu item for this
      // scene (desktop sidebar or mobile bottom bar) and waits for the destination
      // to exist before a single word is spoken. Never narrate what isn't visible.
      const pointAndClick = async (el) => {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch { /* noop */ }
        const r = el.getBoundingClientRect();
        const x = Math.min(r.left + r.width / 2, window.innerWidth - 40);
        const y = Math.min(r.top + r.height / 2, window.innerHeight - 120);
        setCursor({ x, y, visible: ghostOk(), clicking: false });
        await sleep(650);
        if (token.current) return;
        setCursor((c) => ({ ...c, clicking: true }));
        try { el.click(); } catch { /* noop */ }
        await sleep(320);
        setCursor((c) => ({ ...c, clicking: false }));
      };
      const dest = viewKey ? `/restaurant/${viewKey}` : '/restaurant';
      const atDest = () => window.location.pathname.replace(/\/+$/, '') === dest;
      if (!atDest()) {
        const key = viewKey || 'home';
        const isMobile = window.innerWidth < 1024;
        let navEl = document.querySelector(isMobile
          ? `[data-testid="rest-mobile-nav-${key}"]` : `[data-testid="rest-nav-${key}"]`);
        if (isMobile && (!navEl || navEl.getBoundingClientRect().width === 0)) {
          const more = document.querySelector('[data-testid="rest-mobile-nav-more"]');
          if (more) {
            await pointAndClick(more);
            if (token.current) return;
            await sleep(450);
            navEl = document.querySelector(`[data-testid="rest-mobile-more-${key}"]`);
          }
        }
        if (token.current) return;
        if (navEl && navEl.getBoundingClientRect().width > 0) await pointAndClick(navEl);
        else if (navTo) navTo(dest);
        for (let t = 0; t < 20 && !token.current && !atDest(); t += 1) await sleep(200);
        if (token.current) return;
        await sleep(800); // let the page transition and charts settle before speaking
      }

      for (let si = resumeRef.current.i; si < script.length; si += 1) {
        const step = script[si];
        resumeRef.current.i = si;
        // Re-entering the same step (after a pause) skips the slow choreography — the show flows.
        const revisit = resumeRef.current.seen === `${posKey}#${si}`;
        resumeRef.current.seen = `${posKey}#${si}`;
        if (token.current) return;
        const el = await waitFor(step.sel, token);
        if (!el || token.current) continue;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        await sleep(revisit ? 300 : 750);
        if (token.current) return;
        const r = el.getBoundingClientRect();
        const x = Math.min(r.left + r.width / 2, window.innerWidth - 40);
        const y = Math.min(r.top + r.height / 2, window.innerHeight - 120);
        setCursor({ x, y, visible: ghostOk(), clicking: false });
        await sleep(revisit ? 350 : 700);
        if (token.current) return;
        const line = personalize(isEs ? step.es : step.en, guestName);
        setCaption({ text: line, x, y });
        let voiceDone = null;
        if (voiceOn) {
          try {
            const url = await fetchVoiceUrl(line, lang, voiceRef.current);
            if (token.current) return;
            const played = playUrl(url, 1);
            speakingRef.current = played.audio;
            voiceDone = played.done;
          } catch { voiceDone = null; }
        }
        if (step.click) {
          await sleep(600);
          if (token.current) return;
          setCursor((c) => ({ ...c, clicking: true }));
          const target = el.matches('button, a, select, input') ? el : (el.querySelector('button') || el);
          try { target.click(); } catch { /* noop */ }
          await sleep(420);
          setCursor((c) => ({ ...c, clicking: false }));
        }
        if (voiceDone) {
          await Promise.race([voiceDone, sleep(16000)]);
          await sleep(800);
        } else {
          await sleep(step.click ? 3600 : 4200);
        }
        if (token.current) return;
        setCaption(null);
        await sleep(450);
      }
      resumeRef.current.i = 0; // view fully presented — next entry starts fresh
      setCursor((c) => ({ ...c, visible: false }));
      await sleep(1200);
      if (!token.current && onDone) onDone();
    })();

    return () => {
      token.current = true;
      clearInterval(wander);
      if (speakingRef.current) { speakingRef.current.pause(); speakingRef.current = null; }
      setCaption(null);
      setCursor({ x: -100, y: -100, visible: false, clicking: false });
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, viewKey, runId, isEs, voiceOn, voice, demoSlug]); // a voice change re-reads the current line instantly in the new voice

  if (!active) return null;
  const flipX = caption && caption.x > window.innerWidth - 380;
  const flipY = caption && caption.y > window.innerHeight - 220;
  return (
    <>
      <TeamCameo viewKey={viewKey} runId={runId} isEs={isEs} active={active} />
      <div className="fixed z-[88] pointer-events-none" data-testid="present-ghost-cursor"
        style={{
          left: 0, top: 0, opacity: cursor.visible ? 1 : 0,
          transform: `translate(${cursor.x}px, ${cursor.y}px)`,
          transition: 'transform 1.05s cubic-bezier(0.3,0.9,0.35,1), opacity 0.7s ease',
        }}>
        {cursor.clicking && (
          <span className="absolute -left-5 -top-5 w-10 h-10 rounded-full border-2 border-[#F77F11]"
            style={{ animation: 'kx-click-ripple 0.55s ease-out both' }} />
        )}
        <svg width="26" height="26" viewBox="0 0 24 24" style={{ transform: cursor.clicking ? 'scale(0.82)' : 'scale(1)', transition: 'transform 0.18s ease', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.45))' }}>
          <path d="M5.5 3.2 19 11.6l-6.2 1.2-3.1 5.7z" fill="#fff" stroke="#1a1b1e" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
      </div>
      {caption && (() => {
        const vw = window.innerWidth;
        const isMobile = vw < 640;
        if (isMobile) {
          // Phones: fixed film-style subtitles pinned above the control bar — never cropped, always readable.
          return (
            <div className="fixed z-[87] pointer-events-none inset-x-3 flex justify-center" data-testid="present-caption"
              style={{ bottom: 'calc(env(safe-area-inset-bottom) + 118px)', animation: 'kx-caption-in 0.7s cubic-bezier(0.16,1,0.3,1) both' }}>
              <div className="rounded-2xl px-4 py-3 shadow-2xl border border-white/15 text-center max-w-full"
                style={{ background: 'rgba(18,19,22,0.93)', backdropFilter: 'blur(16px)' }}>
                <p className="text-[9.5px] uppercase tracking-[0.25em] font-bold text-[#F77F11] mb-1">
                  {isEs ? 'Tour en vivo' : 'Live tour'}{voiceOn ? ' · Rachel' : ''}
                </p>
                <p className="text-[13px] leading-relaxed text-white/95" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                  {caption.text}
                </p>
              </div>
            </div>
          );
        }
        const capW = Math.min(320, vw - 24);
        const rawLeft = flipX ? caption.x - 340 : caption.x + 30;
        return (
        <div className="fixed z-[87] pointer-events-none" data-testid="present-caption"
          style={{
            left: Math.max(8, Math.min(rawLeft, vw - capW - 8)),
            top: flipY ? caption.y - 150 : caption.y + 26,
            maxWidth: capW,
            animation: 'kx-caption-in 0.7s cubic-bezier(0.16,1,0.3,1) both',
          }}>
          <div className="rounded-2xl px-4 py-3 shadow-2xl border border-white/15"
            style={{ background: 'rgba(18,19,22,0.93)', backdropFilter: 'blur(16px)' }}>
            <p className="text-[9.5px] uppercase tracking-[0.25em] font-bold text-[#F77F11] mb-1">
              {isEs ? 'Tour en vivo' : 'Live tour'}{voiceOn ? ' · Rachel' : ''}
            </p>
            <p className="text-[13px] leading-relaxed text-white/95" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
              {caption.text}
            </p>
          </div>
        </div>
        );
      })()}
      <style>{`
        @keyframes kx-click-ripple { 0% { transform: scale(0.3); opacity: 0.9; } 100% { transform: scale(1.6); opacity: 0; } }
        @keyframes kx-caption-in { 0% { opacity: 0; transform: translateY(10px); } 100% { opacity: 1; transform: translateY(0); } }
      `}</style>
    </>
  );
}
